import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import './StudentTimetableView.css';

const StudentTimetableView = ({ userClass, userId, autoLoad = false, embedded = true }) => {
  const [timetable, setTimetable] = useState(null);
  const [loading, setLoading] = useState(autoLoad);
  const [viewMode, setViewMode] = useState('today'); // Default to daily view
  const [currentDay, setCurrentDay] = useState(new Date().getDay());
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    if (userClass && autoLoad) {
      fetchTimetable();
    }
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [userClass, autoLoad]);
  
  useEffect(() => {
    if (showMobilePopup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobilePopup]);

  const fetchTimetable = async () => {
    try {
      setLoading(true);
      console.log('Fetching student timetable for class:', userClass);
      
      const response = await fetch(`${API_BASE_URL}/api/timetable/${userClass}?userRole=student&userId=${userId}`);
      
      if (!response.ok) {
        console.log(`Timetable API returned status: ${response.status}`);
        if (response.status === 404) {
          console.log('No timetable found for class:', userClass);
          setTimetable(null);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Student timetable data received:', data);
      
      if (data && data.error) {
        throw new Error(data.error);
      }
      
      // Check if data is null (no timetable found)
      if (!data) {
        console.log('No timetable data returned for class:', userClass);
        setTimetable(null);
        return;
      }
      
      setTimetable(data);
    } catch (error) {
      console.error('Error fetching student timetable:', error);
      setTimetable(null);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPeriod = () => {
    if (!timetable) return null;
    
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const today = days[now.getDay() - 1]; // Adjust for Monday = 0
    
    if (!today || !timetable.schedule[today]) return null;

    return timetable.periods.find((period, index) => {
      const [startHour, startMin] = period.startTime.split(':').map(Number);
      const [endHour, endMin] = period.endTime.split(':').map(Number);
      const startTime = startHour * 60 + startMin;
      const endTime = endHour * 60 + endMin;
      
      return currentTime >= startTime && currentTime <= endTime;
    });
  };

  const getNextPeriod = () => {
    if (!timetable) return null;
    
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const today = days[now.getDay() - 1];
    
    if (!today || !timetable.schedule[today]) return null;

    return timetable.periods.find(period => {
      const [startHour, startMin] = period.startTime.split(':').map(Number);
      const startTime = startHour * 60 + startMin;
      return currentTime < startTime;
    });
  };

  const TodayView = () => {
    const today = days[new Date().getDay() - 1];
    const currentPeriod = getCurrentPeriod();
    const nextPeriod = getNextPeriod();
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    if (!today || !timetable?.schedule[today]) {
      return (
        <div className="today-view">
          <div style={{ textAlign: 'center', padding: '30px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
            <h3 style={{ color: '#0c4a6e', marginBottom: '8px' }}>Today's Schedule</h3>
            <p style={{ color: '#64748b', fontSize: '14px' }}>No classes scheduled for today</p>
          </div>
        </div>
      );
    }

    return (
      <div className="today-view">
        <div style={{ 
          background: 'linear-gradient(135deg, #0ea5e9 0%, #0c4a6e 100%)', 
          color: 'white', 
          padding: '15px 20px', 
          borderRadius: '12px', 
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', fontWeight: '700' }}>
            📅 {dayNames[new Date().getDay() - 1]}
          </h3>
          <p style={{ margin: '0', fontSize: '14px', opacity: '0.9' }}>
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        
        {currentPeriod && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
              border: '2px solid #16a34a',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)',
              animation: 'pulse 2s infinite'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#15803d', textTransform: 'uppercase' }}>🟢 Current Period</span>
                <span style={{ fontSize: '12px', color: '#15803d', fontWeight: '600' }}>{currentPeriod.startTime} - {currentPeriod.endTime}</span>
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#15803d', marginBottom: '4px' }}>
                {currentPeriod.name}
              </div>
              {(() => {
                const currentPeriodIndex = timetable.periods.indexOf(currentPeriod);
                const scheduleItem = timetable.schedule[today][currentPeriodIndex];
                const subject = scheduleItem?.subject ? 
                  timetable.subjects.find(s => s.code === scheduleItem.subject) : null;
                
                return subject ? (
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '800', color: '#1e40af', marginBottom: '2px' }}>
                      {subject.name}
                    </div>
                    <div style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                      {subject.teacherName}
                    </div>
                    {scheduleItem.room && (
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        📍 Room: {scheduleItem.room}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '16px', color: '#64748b', fontStyle: 'italic' }}>Free Period</div>
                );
              })()} 
            </div>
          </div>
        )}

        {nextPeriod && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '2px solid #f59e0b',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#92400e', textTransform: 'uppercase' }}>⏰ Next Period</span>
                <span style={{ fontSize: '12px', color: '#92400e', fontWeight: '600' }}>{nextPeriod.startTime} - {nextPeriod.endTime}</span>
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#92400e' }}>
                {nextPeriod.name}
              </div>
              {(() => {
                const nextPeriodIndex = timetable.periods.indexOf(nextPeriod);
                const scheduleItem = timetable.schedule[today][nextPeriodIndex];
                const subject = scheduleItem?.subject ? 
                  timetable.subjects.find(s => s.code === scheduleItem.subject) : null;
                
                return subject ? (
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e40af', marginTop: '4px' }}>
                      {subject.name}
                    </div>
                    <div style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                      {subject.teacherName}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '14px', color: '#64748b', fontStyle: 'italic', marginTop: '4px' }}>Free Period</div>
                );
              })()} 
            </div>
          </div>
        )}

        <div style={{ marginTop: '25px' }}>
          <h4 style={{ 
            margin: '0 0 15px 0', 
            color: '#1e293b', 
            fontSize: '16px', 
            fontWeight: '700',
            padding: '8px 0',
            borderBottom: '2px solid #e2e8f0'
          }}>📋 Complete Day Schedule</h4>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {timetable.periods.map((period, index) => {
              const scheduleItem = timetable.schedule[today][index];
              const subject = scheduleItem?.subject ? 
                timetable.subjects.find(s => s.code === scheduleItem.subject) : null;
              
              const [startHour, startMin] = period.startTime.split(':').map(Number);
              const [endHour, endMin] = period.endTime.split(':').map(Number);
              const startTime = startHour * 60 + startMin;
              const endTime = endHour * 60 + endMin;
              
              const isActive = currentTime >= startTime && currentTime <= endTime;
              const isPast = currentTime > endTime;
              const isUpcoming = currentTime < startTime;

              return (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 15px',
                  borderRadius: '10px',
                  border: isActive ? '2px solid #16a34a' : '1px solid #e2e8f0',
                  background: isActive 
                    ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' 
                    : isPast 
                    ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                    : 'white',
                  opacity: isPast ? 0.7 : 1,
                  transform: isActive ? 'translateX(4px)' : 'none',
                  boxShadow: isActive ? '0 4px 12px rgba(34, 197, 94, 0.25)' : '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}>
                  <div style={{ flex: '0 0 80px', textAlign: 'left' }}>
                    <div style={{ 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: isActive ? '#15803d' : '#64748b',
                      marginBottom: '2px'
                    }}>
                      {period.name}
                    </div>
                    <div style={{ 
                      fontSize: '11px', 
                      color: isActive ? '#15803d' : '#94a3b8',
                      fontWeight: '500'
                    }}>
                      {period.startTime} - {period.endTime}
                    </div>
                  </div>
                  
                  <div style={{ flex: 1, paddingLeft: '15px' }}>
                    {subject ? (
                      <div>
                        <div style={{ 
                          fontSize: '15px', 
                          fontWeight: '700', 
                          color: isActive ? '#1e40af' : isPast ? '#64748b' : '#1e40af',
                          marginBottom: '2px'
                        }}>
                          {subject.name}
                        </div>
                        <div style={{ 
                          fontSize: '13px', 
                          color: isActive ? '#475569' : '#64748b',
                          fontWeight: '500'
                        }}>
                          {subject.teacherName}
                        </div>
                        {scheduleItem.room && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#64748b',
                            marginTop: '2px'
                          }}>
                            📍 {scheduleItem.room}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#94a3b8', 
                        fontStyle: 'italic'
                      }}>
                        Free Period
                      </div>
                    )}
                  </div>
                  
                  {isActive && (
                    <div style={{ 
                      flex: '0 0 auto',
                      background: '#16a34a',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      Now
                    </div>
                  )}
                  
                  {isUpcoming && currentTime > (startTime - 30) && (
                    <div style={{ 
                      flex: '0 0 auto',
                      background: '#f59e0b',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      Soon
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const GridView = () => {
    const isInPopup = showMobilePopup;
    
    return (
      <div className={`timetable-grid-container ${isInPopup ? 'mobile-popup-grid' : ''}`}>
        <div className="student-timetable-grid">
          <div className="grid-header">
            <div className="time-column">Time</div>
            {dayNames.map(day => (
              <div key={day} className="day-header">{isMobile && isInPopup ? day.substring(0, 3) : day}</div>
            ))}
          </div>
          
          {timetable?.periods?.map((period, periodIndex) => (
            <div key={periodIndex} className="grid-row">
              <div className="time-column">
                <div className="period-name">{isMobile && isInPopup ? period.name.replace('Period ', 'P') : period.name}</div>
                <div className="period-time">{period.startTime} - {period.endTime}</div>
              </div>
              
              {days.map(day => {
                const scheduleItem = timetable.schedule[day]?.[periodIndex];
                const subject = scheduleItem?.subject ? 
                  timetable.subjects.find(s => s.code === scheduleItem.subject) : null;

                return (
                  <div key={day} className="schedule-cell">
                    {subject ? (
                      <div className="subject-card">
                        <div className="subject-name">{subject.name}</div>
                        <div className="teacher-name">{subject.teacherName}</div>
                        {scheduleItem.room && (
                          <div className="room-name">{isMobile && isInPopup ? scheduleItem.room : `Room: ${scheduleItem.room}`}</div>
                        )}
                      </div>
                    ) : (
                      <div className="free-period">Free</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const ListView = () => (
    <div className="student-timetable-list">
      {days.map((day, dayIndex) => (
        <div key={day} className="day-section">
          <h3 className="day-title">{dayNames[dayIndex]}</h3>
          <div className="day-periods">
            {timetable?.periods?.map((period, periodIndex) => {
              const scheduleItem = timetable.schedule[day]?.[periodIndex];
              const subject = scheduleItem?.subject ? 
                timetable.subjects.find(s => s.code === scheduleItem.subject) : null;

              return (
                <div key={periodIndex} className="period-card">
                  <div className="period-header">
                    <span className="period-name">{period.name}</span>
                    <span className="period-time">{period.startTime} - {period.endTime}</span>
                  </div>
                  <div className="period-content">
                    {subject ? (
                      <>
                        <div className="subject">{subject.name}</div>
                        <div className="teacher">{subject.teacherName}</div>
                        {scheduleItem.room && <div className="room">Room: {scheduleItem.room}</div>}
                      </>
                    ) : (
                      <div className="free-period">Free Period</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <p style={{ color: '#0c4a6e', fontSize: '16px', fontWeight: '600' }}>Loading your timetable...</p>
      </div>
    );
  }

  if (!timetable) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
        <h4 style={{ color: '#0c4a6e', marginBottom: '8px' }}>No Timetable Available</h4>
        <p style={{ color: '#0c4a6e', fontSize: '14px', marginBottom: '16px' }}>Your class timetable hasn't been created yet. Please contact your teacher or administrator.</p>
        <div style={{ 
          background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)', 
          padding: '12px', 
          borderRadius: '8px', 
          border: '1px solid #0ea5e9',
          fontSize: '12px',
          color: '#0c4a6e'
        }}>
          <strong>For Teachers:</strong> Create timetables using the "📅 Manage Weekly Timetables" option in Admin Level 1 dashboard.
        </div>
      </div>
    );
  }

  if (embedded && isMobile) {
    return (
      <div>
        <div className="timetable-header" style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ color: '#0c4a6e', fontSize: '16px', fontWeight: '600' }}>Class: {timetable?.className || userClass}</span>
            <div className="view-controls" style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setViewMode('today')}
                style={{
                  background: viewMode === 'today' ? 'linear-gradient(135deg, #0ea5e9 0%, #0c4a6e 100%)' : '#f5f5f5',
                  color: viewMode === 'today' ? 'white' : '#666',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  boxShadow: viewMode === 'today' ? '0 2px 8px rgba(14, 165, 233, 0.3)' : 'none'
                }}
              >
                📅 Today
              </button>
              <button
                onClick={() => setShowMobilePopup(true)}
                style={{
                  background: viewMode === 'grid' ? 'linear-gradient(135deg, #0ea5e9 0%, #0c4a6e 100%)' : '#f5f5f5',
                  color: viewMode === 'grid' ? 'white' : '#666',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  boxShadow: viewMode === 'grid' ? '0 2px 8px rgba(14, 165, 233, 0.3)' : 'none'
                }}
              >
                📊 Week
              </button>
            </div>
          </div>
        </div>

        <div className="timetable-content" style={{ maxHeight: viewMode === 'today' ? 'none' : '400px', overflowY: viewMode === 'today' ? 'visible' : 'auto' }}>
          <TodayView />
        </div>

        {showMobilePopup && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.8)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0c4a6e 100%)',
              color: 'white',
              padding: '12px 15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>📅 {timetable?.className || userClass}</h3>
              <button
                onClick={() => setShowMobilePopup(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                ✕
              </button>
            </div>
            <div style={{
              flex: 1,
              background: 'white',
              overflow: 'auto',
              padding: isMobile ? '10px' : '20px',
              minHeight: 0
            }}>
              <GridView />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="timetable-header" style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ color: '#0c4a6e', fontSize: '16px', fontWeight: '600' }}>Class: {timetable?.className || userClass}</span>
          <div className="view-controls" style={{ display: 'flex', gap: '8px' }}>
            <button
              className={viewMode === 'today' ? 'active' : ''}
              onClick={() => setViewMode('today')}
              style={{
                background: viewMode === 'today' ? 'linear-gradient(135deg, #0ea5e9 0%, #0c4a6e 100%)' : '#f5f5f5',
                color: viewMode === 'today' ? 'white' : '#666',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                boxShadow: viewMode === 'today' ? '0 2px 8px rgba(14, 165, 233, 0.3)' : 'none',
                transition: 'all 0.3s ease'
              }}
            >
              📅 Today
            </button>
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => isMobile && embedded ? setShowMobilePopup(true) : setViewMode('grid')}
              style={{
                background: viewMode === 'grid' ? 'linear-gradient(135deg, #0ea5e9 0%, #0c4a6e 100%)' : '#f5f5f5',
                color: viewMode === 'grid' ? 'white' : '#666',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                boxShadow: viewMode === 'grid' ? '0 2px 8px rgba(14, 165, 233, 0.3)' : 'none',
                transition: 'all 0.3s ease'
              }}
            >
              📊 Full Week
            </button>
          </div>
        </div>
      </div>

      <div className="timetable-content" style={{ maxHeight: viewMode === 'grid' ? 'none' : '400px', overflowY: viewMode === 'grid' ? 'visible' : 'auto' }}>
        {viewMode === 'today' && <TodayView />}
        {viewMode === 'grid' && <GridView />}
        {viewMode === 'list' && <ListView />}
      </div>

      {showMobilePopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.8)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0c4a6e 100%)',
            color: 'white',
            padding: '12px 15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>📅 {timetable?.className || userClass}</h3>
            <button
              onClick={() => setShowMobilePopup(false)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              ✕
            </button>
          </div>
          <div style={{
            flex: 1,
            background: 'white',
            overflow: 'auto',
            padding: isMobile ? '10px' : '20px',
            minHeight: 0
          }}>
            <GridView />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentTimetableView;